// WEEKEND WORK REQUEST - Employee Form
// Allows employees to request approval for weekend work.
// Phase 8: Weekend Work Approval

import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Modal, Form, Alert, Badge } from 'react-bootstrap'
import { format, isWeekend, isFuture, parseISO } from 'date-fns'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { useAuth } from '../../modules/auth'
import {
  createWeekendWorkRequest,
  getUserWeekendWorkRequests,
  cancelWeekendWorkRequest,
  WeekendWorkRequest as WeekendWorkRequestType,
  WeekendWorkRequestInput,
} from '../../../api/WeekendWorkAPI'
import { getProjectCodesDropdown } from '../../../api/TimesheetAPI'

interface ProjectOption {
  value: string
  label: string
}

const WeekendWorkRequest = () => {
  const { currentUser } = useAuth()
  const userId = currentUser?.id || ''

  // State
  const [requests, setRequests] = useState<WeekendWorkRequestType[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [projects, setProjects] = useState<ProjectOption[]>([])

  // Form state
  const [formData, setFormData] = useState<WeekendWorkRequestInput>({
    userId,
    requestDate: '',
    reason: '',
    estimatedHours: 8,
    projectCodeId: undefined,
  })

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})

  // Load requests on mount
  useEffect(() => {
    loadRequests()
    loadProjects()
  }, [userId])

  const loadRequests = async () => {
    if (!userId) return
    setLoading(true)
    try {
      const response = await getUserWeekendWorkRequests({ userId })
      setRequests(response.data.data || [])
    } catch (error: any) {
      toast.error(error.message || 'Failed to load requests')
    } finally {
      setLoading(false)
    }
  }

  const loadProjects = async () => {
    try {
      //Employee login add Timesheet -> Weekend Work -> New Request not show project. 
      // const response = await getProjectCodesDropdown()
      // const data = response.data || []
      //Employee login add Timesheet -> Weekend Work -> New Request all project. 
      const response = await getProjectCodesDropdown()
      const data = response.data?.data || []
      
      const options = data.map((p: any) => ({
        value: p.id,
        label: `${p.code} - ${p.name}`,
      }))
      setProjects(options)
    } catch (error) {
      console.error('Failed to load projects', error)
    }
  }

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!formData.requestDate) {
      errors.requestDate = 'Date is required'
    } else {
      const date = new Date(formData.requestDate)
      // Removed isWeekend check to allow requests for all days
      if (!isFuture(date)) {
        errors.requestDate = 'Cannot request for past dates'
      }
    }

    if (!formData.reason || formData.reason.trim().length < 10) {
      errors.reason = 'Reason must be at least 10 characters'
    }

    if (formData.estimatedHours < 1 || formData.estimatedHours > 12) {
      errors.estimatedHours = 'Hours must be between 1 and 12'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    try {
      await createWeekendWorkRequest({
        ...formData,
        userId,
      })
      toast.success('Weekend work request submitted successfully!')
      setShowModal(false)
      resetForm()
      loadRequests()
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit request')
    }
  }

  const handleCancel = async (requestId: string) => {
    const result = await Swal.fire({
      title: 'Cancel Request?',
      text: 'Are you sure you want to cancel this weekend work request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it',
    })

    if (result.isConfirmed) {
      try {
        await cancelWeekendWorkRequest(requestId, { userId })
        toast.success('Request cancelled successfully')
        loadRequests()
      } catch (error: any) {
        toast.error(error.message || 'Failed to cancel request')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      userId,
      requestDate: '',
      reason: '',
      estimatedHours: 8,
      projectCodeId: undefined,
    })
    setFormErrors({})
  }

  const openModal = () => {
    resetForm()
    setShowModal(true)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'warning'
      case 'APPROVED':
        return 'success'
      case 'REJECTED':
        return 'danger'
      case 'CANCELLED':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  return (
    <>
      {/* Page Header */}
      <Row className='mb-4'>
        <Col>
          <div className='d-flex justify-content-between align-items-center'>
            <div>
              <h2 className='fw-bold mb-1'>Weekend Work Requests</h2>
              <p className='text-muted mb-0'>
                Request approval to work on weekends and track your submissions
              </p>
            </div>
            {/* <Button variant='primary' onClick={openModal}>
              <i className='fas fa-plus me-2'></i>
              New Request
            </Button> */}
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3}>
          <Card className='border-warning'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-warning me-3'>
                  <i className='bi bi-clock-history'></i>
                </div>
                <div>
                  <div className='text-muted small'>Pending</div>
                  <div className='fs-4 fw-bold'>
                    {requests.filter((r) => r.status === 'PENDING').length}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-success'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-success me-3'>
                  <i className='bi bi-check-circle'></i>
                </div>
                <div>
                  <div className='text-muted small'>Approved</div>
                  <div className='fs-4 fw-bold'>
                    {requests.filter((r) => r.status === 'APPROVED').length}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-danger'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-danger me-3'>
                  <i className='bi bi-x-circle'></i>
                </div>
                <div>
                  <div className='text-muted small'>Rejected</div>
                  <div className='fs-4 fw-bold'>
                    {requests.filter((r) => r.status === 'REJECTED').length}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className='border-info'>
            <Card.Body>
              <div className='d-flex align-items-center'>
                <div className='fs-2 text-info me-3'>
                  <i className='bi bi-calendar-event'></i>
                </div>
                <div>
                  <div className='text-muted small'>Total Requests</div>
                  <div className='fs-4 fw-bold'>{requests.length}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Requests List */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Card.Title className='mb-0'>Your Weekend Work Requests</Card.Title>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : requests.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-inbox fs-1 text-muted'></i>
                  <p className='text-muted mt-3'>No weekend work requests yet</p>
                  <Button variant='primary' onClick={openModal}>
                    Create Your First Request
                  </Button>
                </div>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-hover'>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Reason</th>
                        <th>Hours</th>
                        <th>Project</th>
                        <th>Status</th>
                        <th>Reviewer</th>
                        <th>Remarks</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr key={request.id}>
                          <td>
                            <strong>{format(parseISO(request.requestDate), 'MMM dd, yyyy')}</strong>
                            <br />
                            <small className='text-muted'>
                              {format(parseISO(request.requestDate), 'EEEE')}
                            </small>
                          </td>
                          <td>
                            <div style={{ maxWidth: '200px' }}>{request.reason}</div>
                          </td>
                          <td>{request.estimatedHours}h</td>
                          <td>
                            {request.projectCode ? (
                              <span>
                                {request.projectCode.code}
                                <br />
                                <small className='text-muted'>{request.projectCode.name}</small>
                              </span>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={getStatusColor(request.status)}>{request.status}</Badge>
                            {request.compOffGranted && (
                              <Badge bg='info' className='ms-1'>
                                Comp-Off: {request.compOffHours}h
                              </Badge>
                            )}
                          </td>
                          <td>
                            {request.reviewer ? (
                              <span>
                                {request.reviewer.firstName} {request.reviewer.lastName}
                                <br />
                                <small className='text-muted'>
                                  {request.reviewedAt
                                    ? format(parseISO(request.reviewedAt), 'MMM dd, HH:mm')
                                    : '-'}
                                </small>
                              </span>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                          <td>
                            {request.status === 'APPROVED' && request.approvalRemarks ? (
                              <small className='text-success'>{request.approvalRemarks}</small>
                            ) : request.status === 'REJECTED' && request.rejectionReason ? (
                              <small className='text-danger'>{request.rejectionReason}</small>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                          <td>
                            {request.status === 'PENDING' && (
                              <Button
                                size='sm'
                                variant='outline-danger'
                                onClick={() => handleCancel(request.id)}
                              >
                                Cancel
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Request Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Request Weekend Work</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant='info'>
              <i className='bi bi-info-circle me-2'></i>
              Weekend work requires manager approval before you can log hours. Please submit your
              request in advance.
            </Alert>

            <Form.Group className='mb-3'>
              <Form.Label>
                Weekend Date <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='date'
                value={formData.requestDate}
                onChange={(e) => setFormData({ ...formData, requestDate: e.target.value })}
                isInvalid={!!formErrors.requestDate}
              />
              <Form.Control.Feedback type='invalid'>{formErrors.requestDate}</Form.Control.Feedback>
              <Form.Text className='text-muted'>
                Select a date in the future
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>
                Reason for Weekend Work <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                isInvalid={!!formErrors.reason}
                placeholder='Explain why you need to work on this weekend...'
              />
              <Form.Control.Feedback type='invalid'>{formErrors.reason}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>
                Estimated Hours <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='number'
                min='1'
                max='12'
                value={formData.estimatedHours}
                onChange={(e) =>
                  setFormData({ ...formData, estimatedHours: parseInt(e.target.value) })
                }
                isInvalid={!!formErrors.estimatedHours}
              />
              <Form.Control.Feedback type='invalid'>
                {formErrors.estimatedHours}
              </Form.Control.Feedback>
              <Form.Text className='text-muted'>How many hours do you plan to work? (1-12)</Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Project (Optional)</Form.Label>
              <Form.Select
                value={formData.projectCodeId || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    projectCodeId: e.target.value || undefined,
                  })
                }
              >
                <option value=''>Select project (optional)</option>
                {projects.map((project) => (
                  <option key={project.value} value={project.value}>
                    {project.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className='text-muted'>
                Select the project you'll be working on (if applicable)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant='primary' type='submit'>
              Submit Request
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default WeekendWorkRequest

